/**
 * TasksPageCalendar Component
 *
 * Calendar-style task management with Month, Week, and Day views.
 * Traditional calendar interface for scheduling overview.
 *
 * Features:
 * - Month view: Full calendar grid with task dots
 * - Week view: 7-day horizontal layout with task cards
 * - Day view: Single day with full task details
 * - View toggle (Month/Week/Day)
 * - Navigation based on current view
 *
 * US-PE-003: Tasks Page Redesign - Calendar View
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import { toLocalDateString } from '../../utils/dateUtils'
import { Link, useSearchParams } from 'react-router-dom'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { supabase } from '@/supabase-client'
import {
  useTasksInDateRange,
  useTaskInstanceMutations,
  useOverridePreview
} from '@/hooks/useTaskInstances'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Calendar,
  CalendarDays,
  CalendarRange,
  MoreVertical,
  SkipForward,
  RotateCcw,
  GripVertical,
  Clock,
  MapPin
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable
} from '@dnd-kit/core'

// Event type styling
const EVENT_TYPE_COLORS = {
  inoculation: 'bg-blue-500',
  transfer: 'bg-purple-500',
  block_creation: 'bg-amber-500',
  pinning: 'bg-emerald-500',
  harvest: 'bg-red-500',
  state_change: 'bg-gray-500',
  quality_check: 'bg-cyan-500',
  inspection: 'bg-indigo-500',
  manual: 'bg-slate-500'
}

const EVENT_TYPE_CONFIG = {
  inoculation: { label: 'Inoc', colour: 'bg-blue-100 text-blue-700 border-blue-200' },
  transfer: { label: 'Transfer', colour: 'bg-purple-100 text-purple-700 border-purple-200' },
  block_creation: { label: 'Blocks', colour: 'bg-amber-100 text-amber-700 border-amber-200' },
  pinning: { label: 'Pinning', colour: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  harvest: { label: 'Harvest', colour: 'bg-red-100 text-red-700 border-red-200' },
  state_change: { label: 'State', colour: 'bg-gray-100 text-gray-700 border-gray-200' },
  quality_check: { label: 'QC', colour: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  inspection: { label: 'Inspect', colour: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  manual: { label: 'Task', colour: 'bg-slate-100 text-slate-700 border-slate-200' }
}

/**
 * Get start and end dates for the current view
 */
function getDateRange(currentDate, view) {
  const date = new Date(currentDate)
  date.setHours(0, 0, 0, 0)

  if (view === 'day') {
    // 3-day view: yesterday, today, tomorrow (centered on selected date)
    const start = new Date(date)
    start.setDate(date.getDate() - 1)
    const end = new Date(date)
    end.setDate(date.getDate() + 1)
    return {
      start: toLocalDateString(start),
      end: toLocalDateString(end)
    }
  }

  if (view === 'week') {
    // Start from Monday
    const day = date.getDay()
    const diff = date.getDate() - day + (day === 0 ? -6 : 1)
    const monday = new Date(date)
    monday.setDate(diff)
    const sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)
    return {
      start: toLocalDateString(monday),
      end: toLocalDateString(sunday)
    }
  }

  // Month view - get full calendar grid (includes days from prev/next months)
  const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1)

  // Start from Monday of the week containing first of month
  const startDay = firstOfMonth.getDay()
  const startOffset = startDay === 0 ? -6 : 1 - startDay
  const gridStart = new Date(firstOfMonth)
  gridStart.setDate(firstOfMonth.getDate() + startOffset)

  // End on Sunday of the week containing last of month (always show 6 weeks)
  const gridEnd = new Date(gridStart)
  gridEnd.setDate(gridStart.getDate() + 41) // 6 weeks - 1 day

  return {
    start: toLocalDateString(gridStart),
    end: toLocalDateString(gridEnd)
  }
}

/**
 * Get array of dates for month grid (42 days - 6 weeks)
 */
function getMonthGrid(currentDate) {
  const date = new Date(currentDate)
  const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1)

  const startDay = firstOfMonth.getDay()
  const startOffset = startDay === 0 ? -6 : 1 - startDay
  const gridStart = new Date(firstOfMonth)
  gridStart.setDate(firstOfMonth.getDate() + startOffset)

  const dates = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart)
    d.setDate(gridStart.getDate() + i)
    const dayOfWeek = d.getDay()
    dates.push({
      date: toLocalDateString(d),
      day: d.getDate(),
      isCurrentMonth: d.getMonth() === date.getMonth(),
      isToday: d.toDateString() === new Date().toDateString(),
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6 // Sunday or Saturday
    })
  }
  return dates
}

/**
 * Get array of dates for week
 */
function getWeekDates(currentDate) {
  const date = new Date(currentDate)
  const day = date.getDay()
  const diff = date.getDate() - day + (day === 0 ? -6 : 1)
  const monday = new Date(date)
  monday.setDate(diff)

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const dates = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    d.setHours(0, 0, 0, 0)
    dates.push({
      date: toLocalDateString(d),
      day: d.getDate(),
      dayName: d.toLocaleDateString('en-GB', { weekday: 'short' }),
      month: d.toLocaleDateString('en-GB', { month: 'short' }),
      isToday: d.toDateString() === today.toDateString(),
      isPast: d < today
    })
  }
  return dates
}

/**
 * Get array of dates for 3-day view (centered on current date)
 */
function getThreeDayDates(currentDate) {
  const date = new Date(currentDate)
  date.setHours(0, 0, 0, 0)

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const dates = []
  for (let i = -1; i <= 1; i++) {
    const d = new Date(date)
    d.setDate(date.getDate() + i)
    d.setHours(0, 0, 0, 0)
    dates.push({
      date: toLocalDateString(d),
      day: d.getDate(),
      dayName: d.toLocaleDateString('en-GB', { weekday: 'long' }),
      month: d.toLocaleDateString('en-GB', { month: 'short' }),
      fullMonth: d.toLocaleDateString('en-GB', { month: 'long' }),
      year: d.getFullYear(),
      isToday: d.toDateString() === today.toDateString(),
      isPast: d < today,
      isCenter: i === 0
    })
  }
  return dates
}

/**
 * Check if task is overdue
 */
function isTaskOverdue(task) {
  if (task.status !== 'pending') return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const scheduledDate = new Date(task.scheduled_date)
  scheduledDate.setHours(0, 0, 0, 0)
  return scheduledDate < today
}

/**
 * Sort tasks
 */
function sortTasks(tasks) {
  return [...tasks].sort((a, b) => {
    const aOverdue = isTaskOverdue(a)
    const bOverdue = isTaskOverdue(b)
    if (aOverdue && !bOverdue) return -1
    if (!aOverdue && bOverdue) return 1

    const statusOrder = { pending: 0, completed: 1, skipped: 2 }
    return (statusOrder[a.status] ?? 1) - (statusOrder[b.status] ?? 1)
  })
}

/**
 * View toggle buttons
 */
function ViewToggle({ view, onChange }) {
  return (
    <div className="flex items-center border rounded-lg p-0.5 bg-muted/50">
      <button
        onClick={() => onChange('month')}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
          view === 'month' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
        )}
      >
        <CalendarRange className="h-4 w-4" />
        Month
      </button>
      <button
        onClick={() => onChange('week')}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
          view === 'week' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
        )}
      >
        <CalendarDays className="h-4 w-4" />
        Week
      </button>
      <button
        onClick={() => onChange('day')}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
          view === 'day' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
        )}
      >
        <Calendar className="h-4 w-4" />
        Day
      </button>
    </div>
  )
}

/**
 * Calendar controls (view toggle + navigation)
 */
function CalendarControls({ view, onViewChange, onPrevious, onNext, onToday, onRefresh, loading }) {
  return (
    <div className="flex items-center gap-3">
      <ViewToggle view={view} onChange={onViewChange} />
      <div className="flex items-center gap-1">
        <Button variant="outline" size="icon" onClick={onPrevious}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={onToday} className="min-w-[70px]">
          Today
        </Button>
        <Button variant="outline" size="icon" onClick={onNext}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <Button variant="outline" size="icon" onClick={onRefresh} disabled={loading}>
        <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
      </Button>
    </div>
  )
}

/**
 * Task dot for month view
 */
function TaskDot({ task }) {
  const eventType = task.event_tasks?.phase_events?.event_type || task.event_type || 'manual'
  const color = EVENT_TYPE_COLORS[eventType] || EVENT_TYPE_COLORS.manual

  return (
    <div
      className={cn(
        'w-2 h-2 rounded-full',
        color,
        task.status === 'completed' && 'opacity-40'
      )}
      title={task.event_tasks?.name || task.name}
    />
  )
}

/**
 * Month day cell
 */
function MonthDayCell({ dateInfo, tasks, onDayClick }) {
  const dayTasks = tasks.filter(t => t.scheduled_date === dateInfo.date)
  const pendingCount = dayTasks.filter(t => t.status === 'pending').length
  const hasOverdue = dayTasks.some(t => isTaskOverdue(t))

  return (
    <button
      onClick={() => onDayClick(dateInfo.date)}
      className={cn(
        'p-1 border-b border-r text-left transition-colors hover:bg-muted/30 relative min-h-16',
        // Non-current month weekdays: lighter background
        !dateInfo.isCurrentMonth && !dateInfo.isWeekend && 'bg-muted/20',
        // Weekend columns always get darker grey background
        dateInfo.isWeekend && 'bg-gray-100'
      )}
    >
      {/* Day number in top right corner */}
      <div className="absolute top-1 right-1.5">
        <span
          className={cn(
            'text-sm font-medium w-6 h-6 flex items-center justify-center rounded-full',
            // Today: red circle with white text
            dateInfo.isToday && 'bg-red-500 text-white',
            // Non-current month: much lighter grey text
            !dateInfo.isCurrentMonth && !dateInfo.isToday && 'text-gray-300',
            // Current month, not today: normal text
            dateInfo.isCurrentMonth && !dateInfo.isToday && 'text-foreground'
          )}
        >
          {dateInfo.day}
        </span>
      </div>

      {/* Task dots at bottom */}
      <div className="absolute bottom-1 left-1 right-1">
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-0.5">
            {dayTasks.slice(0, 4).map(task => (
              <TaskDot key={task.id} task={task} />
            ))}
            {dayTasks.length > 4 && (
              <span className="text-[9px] text-muted-foreground ml-0.5">+{dayTasks.length - 4}</span>
            )}
          </div>
          {pendingCount > 0 && (
            <Badge
              variant="outline"
              className={cn(
                'text-[9px] px-1 py-0 h-4',
                hasOverdue ? 'bg-red-50 text-red-700 border-red-200' : 'bg-slate-50'
              )}
            >
              {pendingCount}
            </Badge>
          )}
        </div>
      </div>
    </button>
  )
}

/**
 * Month view grid
 */
function MonthView({ currentDate, tasks, onDayClick, controls }) {
  const monthGrid = getMonthGrid(currentDate)
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  // Format month header
  const monthHeader = currentDate.toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric'
  })

  return (
    <div className="flex flex-col flex-1 min-h-0 border rounded-lg overflow-hidden">
      {/* Header row with title and controls */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30 shrink-0">
        <h2 className="text-lg font-semibold">{monthHeader}</h2>
        {controls}
      </div>

      {/* Day of week header row */}
      <div className="grid grid-cols-7 border-b shrink-0">
        {weekDays.map((day, i) => (
          <div
            key={day}
            className={cn(
              'px-2 py-1.5 text-xs font-medium text-center border-r last:border-r-0',
              // Weekend headers slightly muted
              (i === 5 || i === 6) ? 'text-muted-foreground/70' : 'text-muted-foreground'
            )}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid - fills remaining space with 6 equal rows */}
      <div
        className="grid grid-cols-7 flex-1 min-h-0"
        style={{ gridTemplateRows: 'repeat(6, 1fr)' }}
      >
        {monthGrid.map(dateInfo => (
          <MonthDayCell
            key={dateInfo.date}
            dateInfo={dateInfo}
            tasks={tasks}
            onDayClick={onDayClick}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Compact task card content (shared between normal and drag overlay)
 */
function WeekTaskCardContent({ task, onComplete, onReset, isUpdating, isDragging, dragHandleProps }) {
  const eventTask = task.event_tasks || {}
  const phaseEvent = eventTask.phase_events || {}
  const processPhase = phaseEvent.process_phases || {}
  const productionProcess = processPhase.production_processes || {}
  const batch = task.production_batches || {}

  const taskName = eventTask.name || task.name || 'Task'
  const batchCode = batch.batch_code
  const eventType = phaseEvent.event_type || task.event_type || 'manual'
  const processId = productionProcess.id
  const isCompleted = task.status === 'completed'
  const isSkipped = task.status === 'skipped'
  const isPending = task.status === 'pending'
  const overdue = isTaskOverdue(task)

  const eventConfig = EVENT_TYPE_CONFIG[eventType] || EVENT_TYPE_CONFIG.manual

  return (
    <div
      className={cn(
        'group flex items-start gap-1.5 p-1.5 rounded border text-xs transition-all',
        overdue && 'border-red-300 bg-red-50',
        isCompleted && 'border-border bg-muted/50 opacity-50',
        isSkipped && 'border-amber-200 bg-amber-50/50 opacity-50',
        isPending && !overdue && 'border-border bg-white hover:bg-muted/30',
        isUpdating && 'opacity-40 pointer-events-none',
        isDragging && 'opacity-50 border-dashed'
      )}
    >
      {/* Drag handle */}
      <div
        {...dragHandleProps}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </div>

      <Checkbox
        checked={isCompleted}
        onCheckedChange={(checked) => {
          if (checked && isPending) onComplete?.(task.id)
          else if (!checked && isCompleted) onReset?.(task.id)
        }}
        disabled={isUpdating || isSkipped}
        className="h-3.5 w-3.5 mt-0.5"
      />

      <div className="flex-1 min-w-0">
        {processId ? (
          <Link
            to={`/production/process/${processId}`}
            className={cn(
              'font-medium truncate block hover:text-primary hover:underline transition-colors',
              (isCompleted || isSkipped) && 'line-through text-muted-foreground'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {taskName}
          </Link>
        ) : (
          <p className={cn(
            'font-medium truncate',
            (isCompleted || isSkipped) && 'line-through text-muted-foreground'
          )}>
            {taskName}
          </p>
        )}
        {batchCode && (
          <Link
            to={`/production/timeline?batch=${encodeURIComponent(batchCode)}`}
            className="text-[10px] text-muted-foreground truncate block hover:text-primary hover:underline transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            {batchCode}
          </Link>
        )}
      </div>

      <span className={cn(
        'px-1 py-0 rounded text-[9px] font-medium border flex-shrink-0',
        eventConfig.colour
      )}>
        {eventConfig.label}
      </span>
    </div>
  )
}

/**
 * Draggable task card for week view
 */
function DraggableWeekTaskCard({ task, onComplete, onReset, isUpdating }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging
  } = useDraggable({
    id: task.id,
    data: { task }
  })

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: isDragging ? 50 : undefined
  } : undefined

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <WeekTaskCardContent
        task={task}
        onComplete={onComplete}
        onReset={onReset}
        isUpdating={isUpdating}
        isDragging={isDragging}
        dragHandleProps={listeners}
      />
    </div>
  )
}

/**
 * Droppable day column for week view
 */
function DroppableDayColumn({ dateInfo, children, isOver }) {
  return (
    <div
      className={cn(
        'flex flex-col rounded-lg border min-h-[400px] transition-colors',
        dateInfo.isToday ? 'border-primary/50 bg-primary/5' : 'border-border',
        isOver && 'border-primary border-2 bg-primary/10'
      )}
    >
      {children}
    </div>
  )
}

/**
 * Droppable day column content - table-like layout with day number in cell
 */
function DroppableDayColumnContent({ dateInfo, dayTasks, completedCount, onComplete, onReset, updatingTaskId, onDayClick }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${dateInfo.date}`,
    data: { date: dateInfo.date }
  })

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex flex-col flex-1 min-h-0 transition-all border-r last:border-r-0',
        // Past days are muted
        dateInfo.isPast && 'bg-muted/20',
        // Today highlight
        dateInfo.isToday && 'bg-primary/5',
        // Drop target highlight
        isOver && 'bg-primary/10 ring-2 ring-inset ring-primary/30'
      )}
    >
      {/* Cell header with day name (left) and day number (right) */}
      <button
        onClick={() => onDayClick(dateInfo.date)}
        className="p-2 h-14 flex items-start justify-between hover:bg-muted/30 transition-colors shrink-0"
      >
        {/* Day name on left */}
        <span
          className={cn(
            'text-sm font-medium',
            dateInfo.isToday ? 'text-red-600' : 'text-muted-foreground'
          )}
        >
          {dateInfo.dayName}
        </span>
        {/* Day number on right */}
        <span
          className={cn(
            'text-lg font-semibold w-8 h-8 flex items-center justify-center rounded-full',
            dateInfo.isToday && 'bg-red-500 text-white',
            dateInfo.isPast && !dateInfo.isToday && 'text-muted-foreground',
            !dateInfo.isPast && !dateInfo.isToday && 'text-foreground'
          )}
        >
          {dateInfo.day}
        </span>
      </button>

      {/* Tasks - scrollable area fills remaining height */}
      <div className="flex-1 p-1.5 space-y-1 overflow-y-auto min-h-0 border-t">
        {dayTasks.length === 0 ? (
          <div className={cn(
            'flex items-center justify-center h-full text-xs text-muted-foreground',
            isOver && 'text-primary font-medium'
          )}>
            {isOver ? 'Drop here' : ''}
          </div>
        ) : (
          dayTasks.map(task => (
            <DraggableWeekTaskCard
              key={task.id}
              task={task}
              onComplete={onComplete}
              onReset={onReset}
              isUpdating={updatingTaskId === task.id}
            />
          ))
        )}
      </div>
    </div>
  )
}

/**
 * Week view with drag-and-drop rescheduling - table-like layout
 */
function WeekView({ currentDate, tasks, onComplete, onReset, updatingTaskId, onDayClick, onReschedule, controls }) {
  const weekDates = getWeekDates(currentDate)
  const [activeTask, setActiveTask] = useState(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5 // Require 5px movement before drag starts
      }
    })
  )

  const handleDragStart = (event) => {
    const { active } = event
    setActiveTask(active.data.current?.task || null)
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    setActiveTask(null)

    if (!over) return

    const taskId = active.id
    const targetDate = over.data.current?.date

    if (!targetDate) return

    // Get the task's current date
    const task = tasks.find(t => t.id === taskId)
    if (!task || task.scheduled_date === targetDate) return

    // Reschedule the task
    onReschedule?.(taskId, targetDate)
  }

  // Calculate week range for header
  const startStr = new Date(weekDates[0].date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  const endStr = new Date(weekDates[6].date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

  // ISO week number
  const getISOWeek = (dateStr) => {
    const d = new Date(dateStr)
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7))
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
  }
  const weekNumber = getISOWeek(weekDates[0].date)

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {/* Table-like container that extends to fill space */}
      <div className="flex flex-col flex-1 min-h-0 border rounded-lg overflow-hidden">
        {/* Header row with date range and controls */}
        <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30 shrink-0">
          <h2 className="text-lg font-semibold">{startStr} – {endStr} <span className="text-muted-foreground font-normal text-sm ml-1">W{weekNumber}</span></h2>
          {controls}
        </div>

        {/* 7-day grid that fills remaining space */}
        <div className="flex flex-1 min-h-0">
          {weekDates.map(dateInfo => {
            const dayTasks = sortTasks(tasks.filter(t => t.scheduled_date === dateInfo.date))
            const completedCount = dayTasks.filter(t => t.status === 'completed').length

            return (
              <DroppableDayColumnContent
                key={dateInfo.date}
                dateInfo={dateInfo}
                dayTasks={dayTasks}
                completedCount={completedCount}
                onComplete={onComplete}
                onReset={onReset}
                updatingTaskId={updatingTaskId}
                onDayClick={onDayClick}
              />
            )
          })}
        </div>
      </div>

      {/* Drag overlay for smooth dragging */}
      <DragOverlay>
        {activeTask ? (
          <div className="opacity-90 shadow-lg">
            <WeekTaskCardContent
              task={activeTask}
              isDragging={false}
              dragHandleProps={{}}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

/**
 * Mini Calendar picker for day view
 */
function MiniCalendar({ currentDate, onDateSelect }) {
  const [displayMonth, setDisplayMonth] = useState(new Date(currentDate))
  const monthGrid = getMonthGrid(displayMonth)
  const weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

  const handlePrevMonth = () => {
    setDisplayMonth(prev => {
      const d = new Date(prev)
      d.setMonth(d.getMonth() - 1)
      return d
    })
  }

  const handleNextMonth = () => {
    setDisplayMonth(prev => {
      const d = new Date(prev)
      d.setMonth(d.getMonth() + 1)
      return d
    })
  }

  const monthHeader = displayMonth.toLocaleDateString('en-GB', {
    month: 'short',
    year: 'numeric'
  })

  const selectedDateStr = toLocalDateString(new Date(currentDate))

  return (
    <div className="w-56 bg-white border rounded-lg shadow-sm p-2">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={handlePrevMonth}
          className="p-1 hover:bg-muted rounded transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium">{monthHeader}</span>
        <button
          onClick={handleNextMonth}
          className="p-1 hover:bg-muted rounded transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {weekDays.map((day, i) => (
          <div key={i} className="text-center text-[10px] text-muted-foreground font-medium py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-0.5">
        {monthGrid.map(dateInfo => {
          const isSelected = dateInfo.date === selectedDateStr
          return (
            <button
              key={dateInfo.date}
              onClick={() => onDateSelect(dateInfo.date)}
              className={cn(
                'w-7 h-7 text-xs rounded-full flex items-center justify-center transition-colors',
                !dateInfo.isCurrentMonth && 'text-muted-foreground/50',
                dateInfo.isToday && !isSelected && 'bg-red-100 text-red-600 font-medium',
                isSelected && 'bg-primary text-primary-foreground font-medium',
                !isSelected && dateInfo.isCurrentMonth && 'hover:bg-muted'
              )}
            >
              {dateInfo.day}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Full task card for day view
 */
function DayTaskCard({ task, onComplete, onSkip, onReset, isUpdating }) {
  const eventTask = task.event_tasks || {}
  const phaseEvent = eventTask.phase_events || {}
  const processPhase = phaseEvent.process_phases || {}
  const productionProcess = processPhase.production_processes || {}
  const batch = task.production_batches || {}
  const strain = batch.strains || {}

  const taskName = eventTask.name || task.name || 'Task'
  const batchCode = batch.batch_code
  const strainName = strain.name
  const eventType = phaseEvent.event_type || task.event_type || 'manual'
  const phaseName = processPhase.name
  const processId = productionProcess.id
  const isCompleted = task.status === 'completed'
  const isSkipped = task.status === 'skipped'
  const isPending = task.status === 'pending'
  const overdue = isTaskOverdue(task)

  const eventConfig = EVENT_TYPE_CONFIG[eventType] || EVENT_TYPE_CONFIG.manual

  return (
    <div
      className={cn(
        'group flex items-start gap-3 p-4 rounded-lg border transition-all',
        overdue && 'border-red-200 bg-red-50/50',
        isCompleted && 'border-border bg-muted/30',
        isSkipped && 'border-amber-200 bg-amber-50/30',
        isPending && !overdue && 'border-border bg-card hover:bg-muted/50',
        isUpdating && 'opacity-60 pointer-events-none'
      )}
    >
      <Checkbox
        checked={isCompleted}
        onCheckedChange={(checked) => {
          if (checked && isPending) onComplete?.(task.id)
          else if (!checked && isCompleted) onReset?.(task.id)
        }}
        disabled={isUpdating || isSkipped}
        className={cn(
          'h-5 w-5 mt-0.5',
          isCompleted && 'data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600'
        )}
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            {processId ? (
              <Link
                to={`/production/process/${processId}`}
                className={cn(
                  'font-medium text-sm block hover:text-primary hover:underline transition-colors',
                  (isCompleted || isSkipped) && 'line-through text-muted-foreground'
                )}
              >
                {taskName}
              </Link>
            ) : (
              <h4 className={cn(
                'font-medium text-sm',
                (isCompleted || isSkipped) && 'line-through text-muted-foreground'
              )}>
                {taskName}
              </h4>
            )}
            {batchCode && (
              <Link
                to={`/production/timeline?batch=${encodeURIComponent(batchCode)}`}
                className="text-xs text-muted-foreground mt-0.5 hover:text-primary hover:underline transition-colors block"
              >
                {batchCode}{strainName && ` - ${strainName}`}
              </Link>
            )}
            {phaseName && (
              <p className="text-xs text-muted-foreground">{phaseName}</p>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" disabled={isUpdating}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white shadow-lg">
              {isPending && (
                <DropdownMenuItem onClick={() => onSkip?.(task.id)}>
                  <SkipForward className="h-4 w-4 mr-2" />
                  Skip task
                </DropdownMenuItem>
              )}
              {(isCompleted || isSkipped) && (
                <DropdownMenuItem onClick={() => onReset?.(task.id)}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset to pending
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2 mt-2">
          <span className={cn(
            'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border',
            eventConfig.colour
          )}>
            {eventConfig.label}
          </span>
          {task.notes && (
            <span className="text-xs text-muted-foreground">Has notes</span>
          )}
          {isSkipped && task.skipped_reason && (
            <span className="text-xs text-amber-600">Skipped: {task.skipped_reason}</span>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Group tasks by session type for operator view.
 * Returns array of { sessionTypeId, sessionName, sessionColour, startTime, endTime, location, tasks }
 * sorted by start_time. Tasks without a session go into a "General" group at the end.
 */
function groupTasksBySession(tasks) {
  const sessionMap = new Map()
  const generalTasks = []

  for (const task of tasks) {
    const sessionType = task.session_types
    const timetableSession = task.timetable_sessions

    if (sessionType && sessionType.id) {
      const key = sessionType.id
      if (!sessionMap.has(key)) {
        sessionMap.set(key, {
          sessionTypeId: sessionType.id,
          sessionName: sessionType.name,
          sessionColour: sessionType.colour,
          startTime: timetableSession?.start_time || null,
          endTime: timetableSession?.end_time || null,
          location: timetableSession?.location || null,
          tasks: []
        })
      }
      // Update timetable info if this task has it and previous didn't
      const group = sessionMap.get(key)
      if (!group.startTime && timetableSession?.start_time) {
        group.startTime = timetableSession.start_time
        group.endTime = timetableSession.end_time
        group.location = timetableSession.location
      }
      group.tasks.push(task)
    } else {
      generalTasks.push(task)
    }
  }

  // Sort session groups by start_time, nulls last
  const groups = Array.from(sessionMap.values()).sort((a, b) => {
    if (a.startTime && b.startTime) return a.startTime.localeCompare(b.startTime)
    if (a.startTime) return -1
    if (b.startTime) return 1
    return a.sessionName.localeCompare(b.sessionName)
  })

  // Sort tasks within each group
  for (const group of groups) {
    group.tasks = sortTasks(group.tasks)
  }

  // Add general group at the end if there are unassigned tasks
  if (generalTasks.length > 0) {
    groups.push({
      sessionTypeId: null,
      sessionName: 'General',
      sessionColour: '#9CA3AF',
      startTime: null,
      endTime: null,
      location: null,
      tasks: sortTasks(generalTasks)
    })
  }

  return groups
}

/**
 * Format a time string (HH:MM:SS or HH:MM) for display
 */
function formatSessionTime(timeStr) {
  if (!timeStr) return ''
  // time comes as "08:00:00" from Supabase, show as "08:00"
  return timeStr.slice(0, 5)
}

/**
 * Calculate total estimated minutes for a list of tasks
 */
function totalEstimatedMinutes(tasks) {
  return tasks.reduce((sum, task) => {
    const minutes = task.event_tasks?.estimated_minutes || 0
    return sum + minutes
  }, 0)
}

/**
 * Format minutes as "Xh Ym"
 */
function formatDuration(totalMinutes) {
  if (totalMinutes === 0) return '0m'
  const hours = Math.floor(totalMinutes / 60)
  const mins = totalMinutes % 60
  if (hours === 0) return `${mins}m`
  if (mins === 0) return `${hours}h`
  return `${hours}h ${mins}m`
}

/**
 * Calculate capacity minutes from start/end time strings
 */
function capacityMinutes(startTime, endTime) {
  if (!startTime || !endTime) return 0
  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  return (eh * 60 + em) - (sh * 60 + sm)
}

/**
 * Session group card - displays a session header with its tasks
 */
function SessionGroupCard({ group, onComplete, onSkip, onReset, updatingTaskId }) {
  const usedMinutes = totalEstimatedMinutes(group.tasks)
  const capMinutes = capacityMinutes(group.startTime, group.endTime)
  const pendingCount = group.tasks.filter(t => t.status === 'pending').length
  const completedCount = group.tasks.filter(t => t.status === 'completed').length
  const totalCount = group.tasks.length

  return (
    <div className="rounded-lg border overflow-hidden">
      {/* Session header with colour accent */}
      <div
        className="px-3 py-2 flex items-center justify-between gap-2"
        style={{ borderLeft: `4px solid ${group.sessionColour || '#9CA3AF'}` }}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-sm">{group.sessionName}</h4>
            {group.startTime && group.endTime && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatSessionTime(group.startTime)} - {formatSessionTime(group.endTime)}
              </span>
            )}
            {group.location && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {group.location}
              </span>
            )}
          </div>
          {/* Capacity indicator */}
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-muted-foreground">
              {formatDuration(usedMinutes)}
              {capMinutes > 0 && ` / ${formatDuration(capMinutes)} capacity`}
            </span>
            {totalCount > 0 && (
              <span className="text-xs text-muted-foreground">
                {completedCount}/{totalCount} done
              </span>
            )}
          </div>
        </div>

        {/* Small progress ring */}
        {totalCount > 0 && (
          <div className="flex-shrink-0">
            <Badge
              variant="outline"
              className={cn(
                'text-xs',
                pendingCount === 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : ''
              )}
            >
              {pendingCount === 0 ? 'Done' : `${pendingCount} pending`}
            </Badge>
          </div>
        )}
      </div>

      {/* Task list */}
      <div className="p-2 space-y-2 bg-white/50">
        {group.tasks.map(task => (
          <DayTaskCard
            key={task.id}
            task={task}
            onComplete={onComplete}
            onSkip={onSkip}
            onReset={onReset}
            isUpdating={updatingTaskId === task.id}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Single day column for ThreeDayView - with session grouping
 */
function DayColumn({ dateInfo, tasks, onComplete, onSkip, onReset, updatingTaskId }) {
  const dayTasks = tasks.filter(t => t.scheduled_date === dateInfo.date)
  const completedCount = dayTasks.filter(t => t.status === 'completed').length
  const sessionGroups = groupTasksBySession(dayTasks)

  return (
    <div
      className={cn(
        'flex flex-col flex-1 min-h-0 border-r last:border-r-0',
        dateInfo.isPast && 'bg-muted/20',
        dateInfo.isToday && 'bg-primary/5'
      )}
    >
      {/* Cell header with day name (left) and day number (right) */}
      <div className="p-3 h-20 flex items-start justify-between shrink-0">
        {/* Day name on left */}
        <span
          className={cn(
            'text-base font-medium',
            dateInfo.isToday ? 'text-red-600' : 'text-muted-foreground'
          )}
        >
          {dateInfo.dayName}
        </span>
        {/* Day number on right */}
        <span
          className={cn(
            'text-2xl font-semibold w-10 h-10 flex items-center justify-center rounded-full',
            dateInfo.isToday && 'bg-red-500 text-white',
            dateInfo.isPast && !dateInfo.isToday && 'text-muted-foreground',
            !dateInfo.isPast && !dateInfo.isToday && 'text-foreground'
          )}
        >
          {dateInfo.day}
        </span>
      </div>

      {/* Progress bar between header and tasks */}
      {dayTasks.length > 0 && (
        <div className="px-3 pb-2 shrink-0">
          <div className="h-1 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${(completedCount / dayTasks.length) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Session-grouped tasks */}
      <div className="flex-1 p-2 space-y-3 overflow-y-auto min-h-0 border-t">
        {sessionGroups.length === 0 ? (
          <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
            No tasks
          </div>
        ) : (
          sessionGroups.map(group => (
            <SessionGroupCard
              key={group.sessionTypeId || 'general'}
              group={group}
              onComplete={onComplete}
              onSkip={onSkip}
              onReset={onReset}
              updatingTaskId={updatingTaskId}
            />
          ))
        )}
      </div>
    </div>
  )
}

/**
 * Three-day view (replaces old single DayView)
 */
function ThreeDayView({ currentDate, tasks, onComplete, onSkip, onReset, updatingTaskId, onDateSelect, showMiniCalendar, setShowMiniCalendar, controls }) {
  const threeDays = getThreeDayDates(currentDate)

  // Header shows center day's month and year
  const centerDate = threeDays[1]
  const headerText = `${centerDate.fullMonth} ${centerDate.year}`

  return (
    <div className="flex flex-col flex-1 min-h-0 border rounded-lg overflow-hidden">
      {/* Header with title, controls, and mini calendar toggle */}
      <div className="px-4 py-2 border-b bg-muted/30 shrink-0 flex items-center justify-between">
        <h2 className="text-lg font-semibold">{headerText}</h2>
        <div className="flex items-center gap-3">
          {controls}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowMiniCalendar(!showMiniCalendar)}
              className="gap-2"
            >
              <Calendar className="h-4 w-4" />
              Pick date
            </Button>
            {showMiniCalendar && (
              <div className="absolute right-0 top-full mt-1 z-50">
                <MiniCalendar
                  currentDate={currentDate}
                  onDateSelect={(dateStr) => {
                    onDateSelect(dateStr)
                    setShowMiniCalendar(false)
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Three columns */}
      <div className="flex flex-1 min-h-0">
        {threeDays.map(dateInfo => (
          <DayColumn
            key={dateInfo.date}
            dateInfo={dateInfo}
            tasks={tasks}
            onComplete={onComplete}
            onSkip={onSkip}
            onReset={onReset}
            updatingTaskId={updatingTaskId}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Main Calendar Tasks Page
 */
export default function TasksPageCalendar({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams()

  // Initialize date from URL param if present, otherwise use today
  const [view, setView] = useState(() => {
    const dateParam = searchParams.get('date')
    // If a date is passed, start in day view to show that specific date
    return dateParam ? 'day' : 'week'
  })
  const [currentDate, setCurrentDate] = useState(() => {
    const dateParam = searchParams.get('date')
    if (dateParam) {
      const parsed = new Date(dateParam)
      // Validate the date is valid
      if (!isNaN(parsed.getTime())) {
        return parsed
      }
    }
    return new Date()
  })
  const [updatingTaskId, setUpdatingTaskId] = useState(null)
  const [showMiniCalendar, setShowMiniCalendar] = useState(false)

  // Clear URL params after initial load (so subsequent navigation doesn't keep them)
  useEffect(() => {
    const dateParam = searchParams.get('date')
    const batchParam = searchParams.get('batch')
    if (dateParam || batchParam) {
      // Clear the params after using them
      setSearchParams({}, { replace: true })
    }
  }, []) // Only run on mount

  // Calculate date range based on view
  const { start, end } = useMemo(() => getDateRange(currentDate, view), [currentDate, view])

  // Fetch tasks
  const { tasks, loading, error, refetch } = useTasksInDateRange(start, end)

  // Mutations
  const { completeTask, skipTask, resetTask, updateTask } = useTaskInstanceMutations()

  // Navigation
  const handlePrevious = useCallback(() => {
    setCurrentDate(prev => {
      const date = new Date(prev)
      if (view === 'day') date.setDate(date.getDate() - 1)
      else if (view === 'week') date.setDate(date.getDate() - 7)
      else date.setMonth(date.getMonth() - 1)
      return date
    })
  }, [view])

  const handleNext = useCallback(() => {
    setCurrentDate(prev => {
      const date = new Date(prev)
      if (view === 'day') date.setDate(date.getDate() + 1)
      else if (view === 'week') date.setDate(date.getDate() + 7)
      else date.setMonth(date.getMonth() + 1)
      return date
    })
  }, [view])

  const handleToday = useCallback(() => {
    setCurrentDate(new Date())
  }, [])

  const handleDayClick = useCallback((dateStr) => {
    setCurrentDate(new Date(dateStr))
    setView('day')
  }, [])

  // Handle date selection from mini calendar (stays in day view)
  const handleDateSelect = useCallback((dateStr) => {
    setCurrentDate(new Date(dateStr))
  }, [])

  // Task actions
  const handleComplete = useCallback(async (taskId) => {
    setUpdatingTaskId(taskId)
    try {
      const { error: err } = await completeTask(taskId)
      if (err) toast.error('Failed to complete task')
      else {
        toast.success('Task completed')
        refetch()
      }
    } catch {
      toast.error('Something went wrong')
    } finally {
      setUpdatingTaskId(null)
    }
  }, [completeTask, refetch])

  const handleSkip = useCallback(async (taskId) => {
    setUpdatingTaskId(taskId)
    try {
      const { error: err } = await skipTask(taskId, 'Skipped via calendar')
      if (err) toast.error('Failed to skip task')
      else {
        toast.success('Task skipped')
        refetch()
      }
    } catch {
      toast.error('Something went wrong')
    } finally {
      setUpdatingTaskId(null)
    }
  }, [skipTask, refetch])

  const handleReset = useCallback(async (taskId) => {
    setUpdatingTaskId(taskId)
    try {
      const { error: err } = await resetTask(taskId)
      if (err) toast.error('Failed to reset task')
      else {
        toast.success('Task reset')
        refetch()
      }
    } catch {
      toast.error('Something went wrong')
    } finally {
      setUpdatingTaskId(null)
    }
  }, [resetTask, refetch])

  // Override preview for bump confirmation
  const { previewOverride, preview: bumpPreview, loading: bumpPreviewLoading, clearPreview: clearBumpPreview } = useOverridePreview()
  const [pendingReschedule, setPendingReschedule] = useState(null)
  const [bumpReason, setBumpReason] = useState('')

  // Reschedule task — show confirmation dialog instead of applying immediately
  const handleReschedule = useCallback(async (taskId, newDate) => {
    const task = tasks.find(t => t.id === taskId)
    setPendingReschedule({
      taskId,
      taskName: task?.event_tasks?.name || task?.task_name || 'Task',
      currentDate: task?.scheduled_date,
      newDate
    })
    setBumpReason('')
    previewOverride('task_instance', taskId, 'scheduled_date', newDate).catch(() => {})
  }, [tasks, previewOverride])

  const confirmReschedule = useCallback(async () => {
    if (!pendingReschedule) return
    setUpdatingTaskId(pendingReschedule.taskId)
    try {
      // Use bump_task_instance RPC for audit trail
      const { data, error: err } = await supabase.rpc('bump_task_instance', {
        p_task_id: pendingReschedule.taskId,
        p_new_date: pendingReschedule.newDate,
        p_reason: bumpReason || 'Rescheduled via drag and drop'
      })
      if (err) throw err
      if (data?.success === false) {
        toast.error(data.message || 'Failed to reschedule task')
      } else {
        const formattedDate = new Date(pendingReschedule.newDate).toLocaleDateString('en-GB', {
          weekday: 'short', day: 'numeric', month: 'short'
        })
        toast.success(`Task moved to ${formattedDate}`)
        refetch()
      }
    } catch {
      toast.error('Something went wrong')
    } finally {
      setUpdatingTaskId(null)
      setPendingReschedule(null)
      clearBumpPreview()
    }
  }, [pendingReschedule, bumpReason, refetch, clearBumpPreview])

  const cancelReschedule = useCallback(() => {
    setPendingReschedule(null)
    clearBumpPreview()
    setBumpReason('')
  }, [clearBumpPreview])

  // Shared calendar controls to pass to each view
  const calendarControls = (
    <CalendarControls
      view={view}
      onViewChange={setView}
      onPrevious={handlePrevious}
      onNext={handleNext}
      onToday={handleToday}
      onRefresh={refetch}
      loading={loading}
    />
  )

  return (
    <>
    <PageLayout
      user={user}
      onLogout={onLogout}
      fillHeight
    >
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {error ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            Error loading tasks. Please try again.
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {view === 'month' && (
              <MonthView
                currentDate={currentDate}
                tasks={tasks}
                onDayClick={handleDayClick}
                controls={calendarControls}
              />
            )}
            {view === 'week' && (
              <WeekView
                currentDate={currentDate}
                tasks={tasks}
                onComplete={handleComplete}
                onReset={handleReset}
                updatingTaskId={updatingTaskId}
                onDayClick={handleDayClick}
                onReschedule={handleReschedule}
                controls={calendarControls}
              />
            )}
            {view === 'day' && (
              <ThreeDayView
                currentDate={currentDate}
                tasks={tasks}
                onComplete={handleComplete}
                onSkip={handleSkip}
                onReset={handleReset}
                updatingTaskId={updatingTaskId}
                onDateSelect={handleDateSelect}
                showMiniCalendar={showMiniCalendar}
                setShowMiniCalendar={setShowMiniCalendar}
                controls={calendarControls}
              />
            )}
          </>
        )}
      </div>
    </PageLayout>

      {/* Bump confirmation dialog */}
      <Dialog open={!!pendingReschedule} onOpenChange={(open) => { if (!open) cancelReschedule() }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reschedule Task</DialogTitle>
            <DialogDescription>
              Move "{pendingReschedule?.taskName}" to a new date
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">
                {pendingReschedule?.currentDate && new Date(pendingReschedule.currentDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
              </span>
              <span>→</span>
              <span className="font-medium">
                {pendingReschedule?.newDate && new Date(pendingReschedule.newDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
              </span>
            </div>

            {bumpPreviewLoading && (
              <div className="text-xs text-muted-foreground">Checking capacity...</div>
            )}

            {bumpPreview?.capacity_conflicts?.length > 0 && (
              <Alert variant="destructive">
                <AlertDescription className="text-xs">
                  Target date is at/over capacity ({bumpPreview.capacity_conflicts[0].task_count}/{bumpPreview.capacity_conflicts[0].max_tasks} tasks)
                </AlertDescription>
              </Alert>
            )}

            {bumpPreview && !bumpPreview.capacity_conflicts?.length && !bumpPreviewLoading && (
              <div className="text-xs text-green-600">Target date has capacity</div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="bumpReason" className="text-xs">Reason</Label>
              <Input
                id="bumpReason"
                value={bumpReason}
                onChange={(e) => setBumpReason(e.target.value)}
                placeholder="e.g. Equipment maintenance"
                className="h-8 text-sm"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={cancelReschedule}>Cancel</Button>
            <Button size="sm" onClick={confirmReschedule} disabled={updatingTaskId}>
              {updatingTaskId ? 'Moving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
